import React from 'react';
import { hydrateRoot, createRoot } from 'react-dom/client';
import { getComponent } from './component-registry';

declare global {
  interface Window {
    __REACT_COMPONENTS__?: Array<{
      id: string;
      name: string;
      props: Record<string, unknown>;
    }>;
  }
}

const hydratedComponentIds = new Set<string>();

/**
 * Hydrates SSR-rendered React components. Suppresses React 18 recoverable
 * hydration errors to prevent mismatch-triggered re-renders (prevents flickering content on render).
 */
function hydrateComponents() {
  const components = window.__REACT_COMPONENTS__ || [];
  let missingContainers = 0;

  for (const { id, name, props } of components) {
    if (hydratedComponentIds.has(id)) {
      continue;
    }

    const container = document.getElementById(id);
    const Component = getComponent(name);

    if (!container) {
      missingContainers += 1;
      // eslint-disable-next-line no-console
      console.warn(`Container with id "${id}" not found for component "${name}"`);
      continue;
    }

    if (!Component) {
      // eslint-disable-next-line no-console
      console.error(`Component "${name}" not found in registry`);
      continue;
    }

    try {
      // Check if container has server-rendered content
      if (container.innerHTML.trim() && !container.dataset.reactError) {
        // Suppress minor SSR/client mismatches to prevent subtree re-renders
        hydrateRoot(container, React.createElement(Component, props), {
          onRecoverableError:
            process.env.NODE_ENV === 'production'
              ? () => {} // Silent in production
              : error => {
                  // eslint-disable-next-line no-console
                  console.warn('Hydration mismatch:', error);
                }
        });
      } else {
        // Render client-side only
        createRoot(container).render(React.createElement(Component, props));
      }

      hydratedComponentIds.add(id);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(`Failed to render component "${name}":`, error);
    }
  }

  return missingContainers;
}

// Hydrate immediately and retry on DOMContentLoaded if containers aren't in the DOM yet.
const missingContainers = hydrateComponents();

if (missingContainers > 0 && document.readyState === 'loading') {
  document.addEventListener(
    'DOMContentLoaded',
    () => {
      hydrateComponents();
    },
    { once: true }
  );
}
